import React, { useState } from 'react';
import { ChevronDown, ChevronUp, MessageSquare, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

const FAQs = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const faqs = [
        {
            question: "What areas of law does your firm practice?",
            answer: "Our firm specializes in multiple areas including Criminal Law, Family Law, Civil Law, Business Law, International Law, and Labor Law. We provide comprehensive legal services across these practice areas with dedicated experts in each field."
        },
        {
            question: "How do I schedule a consultation?",
            answer: "You can schedule a consultation by calling our office at +92 332 9754400, filling out the contact form on our website, or sending us an email at chkhurram379@gmail.com. We'll respond promptly to set up a meeting at your convenience."
        },
        {
            question: 'What should I bring to my first consultation?',
            answer: "For your first consultation, please bring any relevant documents related to your case, such as contracts, court documents, correspondence, and any other supporting materials. Also, bring a list of questions you'd like to discuss."
        },
        {
            question: "Do you offer free initial consultations?",
            answer: "Yes, we offer free initial consultations for most cases. This allows us to understand your situation and determine how we can best help you, while giving you the opportunity to learn more about our services without any obligation."
        },
        {
            question: "What are your fees and payment options?",
            answer: "Our fees vary depending on the type and complexity of your case. We offer flexible payment options and will discuss our fee structure during your initial consultation. We strive to provide transparent pricing and value for our services."
        },
        {
            question: "How long will my case take?",
            answer: "The duration of your case depends on various factors including its complexity, court schedules, and opposing parties. During your consultation, we can provide an estimated timeline based on our experience with similar cases."
        }
    ];

    // WhatsApp contact info
    const phoneNumber = '923329754400';
    const message = encodeURIComponent('I am here for free consultation');
    
    const handleWhatsappContact = () => {
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
        window.open(whatsappUrl, '_blank');
    };

    return (
        <div>
            {/* Hero Section */}
            <div className="relative h-[400px] bg-gray-900">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-900 to-indigo-700 opacity-90"></div>
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptNiA2djZoNnYtNmgtNnptLTEyIDBoNnY2aC02di02em0xMiAwaDZ2NmgtNnYtNnoiLz48cGF0aCBkPSJNMTIgMTJoNnY2aC02di02em02IDZoNnY2aC02di02em0wLTZoNnY2aC02di02em0xMiAwaDZ2NmgtNnYtNnptLTEyIDEyaDZ2NmgtNnYtNnptMTIgMGg2djZoLTZ2LTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50"></div>
                <div className="relative container mx-auto px-4 h-full flex items-center">
                    <div>
                        <div className="inline-block bg-white/20 text-white px-4 py-1 rounded-md text-sm font-medium mb-4 backdrop-blur-sm">
                            COMMON QUESTIONS
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Frequently Asked Questions</h1>
                        <p className="text-gray-100 max-w-xl text-lg">
                            Find answers to common questions about our legal services and processes.
                        </p>
                    </div>
                </div>
            </div>

            {/* FAQs Section */}
            <div className="container mx-auto px-4 py-20">
                <div className="max-w-3xl mx-auto">
                    <div className="mb-10">
                        <h2 className="text-3xl font-bold mb-4 text-center">How Can We Help You?</h2>
                        <p className="text-gray-600 text-center mb-8">
                            Browse through these frequently asked questions to find quick answers to common inquiries.
                        </p>
                    </div>
                    
                    <div className="space-y-5">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-all duration-200 hover:shadow-md"
                            >
                                <button
                                    className="w-full px-6 py-5 text-left flex justify-between items-center bg-white transition-colors"
                                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                                    aria-expanded={openIndex === index}
                                    aria-controls={`faq-answer-${index}`}
                                >
                                    <span className="font-semibold text-lg text-gray-800">{faq.question}</span>
                                    <div className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${openIndex === index ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'}`}>
                                        {openIndex === index ? (
                                            <ChevronUp className="w-5 h-5" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5" />
                                        )}
                                    </div>
                                </button>
                                <div 
                                    id={`faq-answer-${index}`}
                                    className={`transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0 overflow-hidden'}`}
                                >
                                    <div className="px-6 py-5 bg-gray-50 border-t border-gray-200">
                                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Contact Section */}
                    <div className="mt-16 bg-indigo-50 rounded-2xl p-8 shadow-sm">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold mb-4 text-gray-800">Still Have Questions?</h2>
                            <p className="text-gray-600 mb-8 max-w-lg mx-auto">
                                Can't find the answer you're looking for? Please reach out to our team for personalized assistance with your legal inquiries.
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center gap-4">
                                <Link
                                    to="/contact"
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center"
                                >
                                    <MessageSquare className="w-5 h-5 mr-2" />
                                    Contact Us
                                </Link>
                                <button
                                    onClick={handleWhatsappContact}
                                    className="border border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center"
                                >
                                    <Phone className="w-5 h-5 mr-2" />
                                    Chat on WhatsApp
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FAQs;